'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  ContactAttribute,
  ContactAttributeList,
} from '@eliadmin/core/src/components/contact/contact-attribute-list';
import { SearchButton } from '@eliadmin/core/src/components/header/search-button';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import { FiUser } from 'react-icons/fi';
import { useRouter } from 'next/navigation';
import Link from '@eliadmin/core/src/components/link';
import { trackUserActions } from '../analytics/analytics-helper';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug ?? '#'} className="flex-1" prefetch={false}>
          <SheetClose
            className={cn(
              'text-left text-xl font-semibold',
              layer === 1 && 'text-[16px] text-lg font-medium',
              layer && layer > 1 && 'text-sm font-normal',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const { t } = useTranslations();
  const { me, isInitialized } = useCustomerStore();
  const customerType = me?.customer?.customer_type?.type;
  const router = useRouter();

  const generalLayout = useGlobalStore(state => state.generalLayout);

  const categories = props.categories;

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label="Otvoriť hlavné menu"
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          {HEADER_CONFIG?.sidebar?.triggerIcon}
        </SheetTrigger>
        <SheetContent
          side={'left'}
          className={cn(
            'flex w-screen flex-col justify-between gap-6 overflow-scroll p-0',
            'ea-mobile-sidebar__content',

            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'left-4 top-6',
            icon: 'text-foreground h-6 w-6 font-bold',
          }}
        >
          <SheetTitle className="sr-only">Hlavné menu</SheetTitle>
          <div
            className={cn(
              'relative flex w-full flex-col gap-6',
              'ea-mobile-sidebar__inner-content px-6 pt-12',
            )}
          >
            <SearchButton />
            <ul
              className={cn(
                'mb-4 overflow-scroll',
                'ea-menu-sidebar__list',
                // className?.list,
              )}
            >
              <Accordion type="single" collapsible>
                {categories?.map((category, index) => (
                  <CategoryItem key={index} category={category} />
                ))}
              </Accordion>
            </ul>
            {!!props.menu && (
              <div className="flex flex-col gap-4">
                {props.menu?.map((item, index) => {
                  const Comp = item.path
                    ? isRelativeHref(getSafeHref(item.path))
                      ? Link
                      : 'a'
                    : 'span';
                  return (
                    <SheetClose key={index} asChild>
                      <Comp
                        href={item.path ? getSafeHref(item.path) : (undefined as any)}
                        className="flex w-full"
                        prefetch={false}
                      >
                        <div className={cn('text-sm', '')}>{item.name}</div>
                      </Comp>
                    </SheetClose>
                  );
                })}
              </div>
            )}
            <Separator />
            {!me && (
              <SheetClose asChild>
                <Button asChild variant={'accent'} className="w-full">
                  <Link prefetchOnHover prefetch={false} href={'/prihlasit-sa'}>
                    {t('text190')}
                  </Link>
                </Button>
              </SheetClose>
            )}
            {me && (
              <div className="flex flex-col gap-2">
                <SheetClose asChild>
                  <Button variant={'outline'} asChild className="w-full bg-secondary text-white">
                    <Link prefetchOnHover prefetch={false} href={ROUTES.PROFILE()}>
                      <FiUser className="mr-2 h-5 w-5" />
                      {t('Profil')}
                    </Link>
                  </Button>
                </SheetClose>
                {customerType === 'user' || customerType === 'student' ? (
                  <SheetClose asChild>
                    <Button variant={'accent'} asChild className="w-full">
                      <Link prefetchOnHover prefetch={false} href={ROUTES.CURRICULUM_CREATE()}>
                        {t('text2251')}
                      </Link>
                    </Button>
                  </SheetClose>
                ) : customerType === 'company' ? (
                  <SheetClose asChild>
                    <Button variant={'accent'} asChild className="w-full">
                      <Link prefetchOnHover prefetch={false} href={'/pridat-ponuku'}>
                        Pridať pracovnú ponuku
                      </Link>
                    </Button>
                  </SheetClose>
                ) : customerType === 'school' ? (
                  <SheetClose asChild>
                    <Button variant={'accent'} asChild className="w-full">
                      <Link prefetchOnHover prefetch={false} href={'/vytvorit-kurz'}>
                        Pridať vzdelávací kurz
                      </Link>
                    </Button>
                  </SheetClose>
                ) : null}
              </div>
            )}
            <Separator />
            <div className="">
              <ContactAttributeList
                className={cn('font-bold', 'ea-contact-page__phone-email-wrapper')}
              >
                <div className="text-sm font-normal">
                  <HtmlRendererRaw
                    as="p"
                    className="text-sm font-bold text-foreground"
                    html={generalLayout.header?.top_bar_text || ''}
                  />
                </div>
                {generalLayout.contact?.phone && (
                  <ContactAttribute
                    className={{ text: 'text-sm font-normal text-primary', icon: 'text-primary' }}
                    icon={CONTACT_CONFIG.phoneIcon}
                    text={
                      <a
                        href={`tel:${generalLayout.contact?.phone}`}
                        onClick={() =>
                          trackUserActions.clickPhone(generalLayout.contact?.phone as string, {
                            link_url: `tel:${generalLayout.contact?.phone}`,
                          })
                        }
                      >
                        {generalLayout.contact?.phone}
                      </a>
                    }
                  />
                )}
                {generalLayout.contact?.email && (
                  <ContactAttribute
                    className={{ text: 'text-sm font-normal text-primary', icon: 'text-primary' }}
                    icon={CONTACT_CONFIG.mailIcon}
                    text={
                      <a
                        href={`mailto:${generalLayout.contact?.email}`}
                        onClick={() =>
                          trackUserActions.clickEmail(generalLayout.contact?.email as string, {
                            link_url: `mailto:${generalLayout.contact?.email}`,
                          })
                        }
                      >
                        {generalLayout.contact?.email}
                      </a>
                    }
                  />
                )}
              </ContactAttributeList>
            </div>
            <Separator />
            <SocialIcons className={{ icon: 'h-5 w-5', button: 'h-12 w-12', wrapper: '' }} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
